import React, { useState, useMemo } from 'react';
import './PortfolioXRay.css';
import { convictionLabel } from '../../utils/convictionLabel';

export default function PortfolioXRay({ scores }) {
  const [expanded, setExpanded] = useState(false);

  const analysis = useMemo(() => {
    if (!scores?.length) return null;

    const totalValue = scores.reduce((sum, s) => sum + (s.position_value || 0), 0);
    if (totalValue <= 0) return null;

    // Position weights
    const weights = scores.map(s => ({
      symbol: s.symbol,
      weight: ((s.position_value || 0) / totalValue) * 100,
      value: s.position_value || 0,
      conviction: s.combined_conviction || 0,
    })).sort((a, b) => b.weight - a.weight);

    // Herfindahl index (sum of squared weights)
    const hhi = weights.reduce((sum, w) => sum + Math.pow(w.weight / 100, 2), 0);

    // Top 5 weight
    const top5Weight = weights.slice(0, 5).reduce((sum, w) => sum + w.weight, 0);

    // Single stock > 25% check
    const overconcentrated = weights.filter(w => w.weight > 25);

    // All same conviction band check
    const bands = new Set(scores.map(s => convictionLabel(s.combined_conviction || 0)));
    const allSameBand = bands.size === 1;

    // Flags
    const flags = [];
    if (overconcentrated.length > 0) {
      flags.push({ severity: 'high', message: `${overconcentrated.map(w => w.symbol).join(', ')} > 25% of portfolio` });
    }
    if (top5Weight > 80) {
      flags.push({ severity: 'medium', message: `Top 5 holdings represent ${top5Weight.toFixed(0)}% of portfolio` });
    }
    if (hhi > 0.25) {
      flags.push({ severity: 'high', message: `High concentration (HHI: ${(hhi * 10000).toFixed(0)})` });
    }
    if (allSameBand && scores.length > 3) {
      flags.push({ severity: 'low', message: `All holdings in same conviction band` });
    }
    if (scores.length < 5) {
      flags.push({ severity: 'medium', message: `Only ${scores.length} positions - limited diversification` });
    }

    return { weights, hhi, top5Weight, overconcentrated, allSameBand, flags, totalValue };
  }, [scores]);

  if (!analysis) return null;

  return (
    <div className="portfolio-xray">
      <button className="xray-toggle" onClick={() => setExpanded(!expanded)}>
        <span>Portfolio X-Ray</span>
        {analysis.flags.length > 0 && (
          <span className="xray-badge">{analysis.flags.length}</span>
        )}
        <span className="xray-arrow">{expanded ? '\u25BC' : '\u25B6'}</span>
      </button>
      {expanded && (
        <div className="xray-content">
          {analysis.flags.length > 0 && (
            <div className="xray-flags">
              {analysis.flags.map((flag, i) => (
                <div key={i} className={`xray-flag ${flag.severity}`}>
                  <span className="flag-icon">{flag.severity === 'high' ? '!' : flag.severity === 'medium' ? '~' : 'i'}</span>
                  {flag.message}
                </div>
              ))}
            </div>
          )}
          <div className="xray-metrics">
            <div className="xray-metric">
              <span className="xray-metric-label">Herfindahl Index</span>
              <span className="xray-metric-value">{(analysis.hhi * 10000).toFixed(0)}</span>
              <span className="xray-metric-note">{analysis.hhi < 0.1 ? 'Well diversified' : analysis.hhi < 0.25 ? 'Moderate concentration' : 'Highly concentrated'}</span>
            </div>
            <div className="xray-metric">
              <span className="xray-metric-label">Top 5 Weight</span>
              <span className="xray-metric-value">{analysis.top5Weight.toFixed(1)}%</span>
            </div>
            <div className="xray-metric">
              <span className="xray-metric-label">Positions</span>
              <span className="xray-metric-value">{scores.length}</span>
            </div>
          </div>
          <div className="xray-weights">
            <h5>Position Weights</h5>
            {analysis.weights.slice(0, 10).map(w => (
              <div key={w.symbol} className="xray-weight-row">
                <span className="xray-weight-symbol">{w.symbol}</span>
                <div className="xray-weight-bar-wrapper">
                  <div className="xray-weight-bar" style={{ width: `${Math.min(100, w.weight)}%` }} />
                </div>
                <span className="xray-weight-pct">{w.weight.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
